<script setup lang="ts">
  // Table multi-filter for the history Mesas tab: checkbox rows for the
  // branch tables (empty selection = all), pending state applied via
  // Aplicar — same interaction as the status popover.
  import { computed, ref } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';

  interface ITableOption {
    id: number;
    name: string;
  }

  const props = defineProps<{
    options: ITableOption[];
    /** Applied table ids (empty = not narrowed). */
    tableIds: number[];
  }>();

  const emit = defineEmits<{
    (e: 'apply', value: number[]): void;
  }>();

  const isNarrowed = computed(() => props.tableIds.length > 0);

  //#region Pending selection (applied on "Aplicar")

  const pending = ref<number[]>([]);

  function syncPendingFromApplied(): void {
    pending.value = [...props.tableIds];
  }

  function handleToggle(open: boolean, toggle: () => void): void {
    if (!open) syncPendingFromApplied();
    toggle();
  }

  function toggleTable(tableId: number): void {
    pending.value = pending.value.includes(tableId)
      ? pending.value.filter((id) => id !== tableId)
      : [...pending.value, tableId];
  }

  function apply(close: () => void): void {
    emit('apply', [...pending.value]);
    close();
  }

  function reset(close: () => void): void {
    pending.value = [];
    emit('apply', []);
    close();
  }

  //#endregion
</script>

<template>
  <PosUiPopover placement="bottom" align="start">
    <template #trigger="{ open, toggle }">
      <PosUiButton
        color="neutral"
        variant="outline"
        size="md"
        icon="table-furniture"
        class="tables-trigger"
        @click="handleToggle(open, toggle)"
      >
        <span class="tables-trigger-label">
          {{ $t('posV2.historyTablesLabel') }}
        </span>
        <span v-if="isNarrowed" class="tables-active-dot" />
      </PosUiButton>
    </template>

    <template #default="{ close }">
      <div class="tables-panel">
        <div class="tables-options">
          <button
            v-for="option in options"
            :key="option.id"
            type="button"
            class="tables-option"
            :class="{ checked: pending.includes(option.id) }"
            @click="toggleTable(option.id)"
          >
            <PosUiIcon
              :name="
                pending.includes(option.id)
                  ? 'checkbox-marked'
                  : 'checkbox-blank-outline'
              "
              class="option-checkbox"
            />
            <span class="option-label">{{ option.name }}</span>
          </button>
        </div>

        <div class="tables-actions">
          <PosUiButton
            color="neutral"
            variant="ghost"
            size="sm"
            @click="reset(close)"
          >
            {{ $t('posV2.historyPriceReset') }}
          </PosUiButton>
          <PosUiButton
            color="primary"
            variant="solid"
            size="sm"
            @click="apply(close)"
          >
            {{ $t('posV2.historyPriceApply') }}
          </PosUiButton>
        </div>
      </div>
    </template>
  </PosUiPopover>
</template>

<style scoped lang="scss">
  .tables-trigger {
    @apply tw-relative;

    .tables-trigger-label {
      @apply tw-hidden md:tw-inline;
    }

    .tables-active-dot {
      @apply tw-absolute tw-right-1 tw-top-1 tw-h-2 tw-w-2 tw-rounded-full tw-bg-orange-600;
    }
  }

  .tables-panel {
    @apply tw-flex tw-flex-col tw-gap-3;

    .tables-options {
      @apply tw-flex tw-flex-col;

      .tables-option {
        @apply tw-flex tw-min-h-[40px] tw-cursor-pointer tw-select-none tw-items-center tw-gap-2 tw-rounded-md tw-px-2 tw-text-left tw-transition-colors;

        &:hover {
          @apply tw-bg-neutral-50;
        }

        .option-checkbox {
          @apply tw-flex-none tw-text-lg tw-text-neutral-400;
        }

        &.checked .option-checkbox {
          @apply tw-text-orange-600;
        }

        .option-label {
          @apply tw-text-sm tw-text-neutral-700;
        }
      }
    }

    // Sticky within the scrolling popover panel: many tables overflow the
    // panel's max-height, but Aplicar must always be visible. Negative
    // margins/offset bleed over the panel's p-3 (sticky offsets respect the
    // scroller's padding) so the bar spans edge to edge and its white bg
    // covers rows scrolling underneath.
    .tables-actions {
      @apply tw-sticky tw--bottom-3 tw-z-10 tw--mx-3 tw--mb-3 tw-flex tw-items-center tw-justify-end tw-gap-2 tw-border-t tw-border-neutral-100 tw-bg-white tw-px-3 tw-py-2 tw-shadow-[0_-4px_6px_-4px_rgba(15,23,42,0.12)];
    }
  }
</style>
